// Free web fonts offered by the Customization font picker.
//
// A curated slice of the Google Fonts catalogue rather than the live API,
// which needs an API key. Every family here is free to use and served from
// fonts.googleapis.com, so a customer's widget can load whatever they pick.

export const SYSTEM_FONT = "System UI";

export const FONTS = [
  // Sans — the default group, and what most widgets want.
  { name: SYSTEM_FONT, category: "Sans" },
  { name: "Inter", category: "Sans" },
  { name: "Roboto", category: "Sans" },
  { name: "Open Sans", category: "Sans" },
  { name: "Lato", category: "Sans" },
  { name: "Montserrat", category: "Sans" },
  { name: "Poppins", category: "Sans" },
  { name: "Raleway", category: "Sans" },
  { name: "Nunito", category: "Sans" },
  { name: "Nunito Sans", category: "Sans" },
  { name: "Work Sans", category: "Sans" },
  { name: "Rubik", category: "Sans" },
  { name: "Karla", category: "Sans" },
  { name: "Mulish", category: "Sans" },
  { name: "Manrope", category: "Sans" },
  { name: "DM Sans", category: "Sans" },
  { name: "Plus Jakarta Sans", category: "Sans" },
  { name: "Figtree", category: "Sans" },
  { name: "Outfit", category: "Sans" },
  { name: "Sora", category: "Sans" },
  { name: "Space Grotesk", category: "Sans" },
  { name: "Urbanist", category: "Sans" },
  { name: "Lexend", category: "Sans" },
  { name: "Public Sans", category: "Sans" },
  { name: "Source Sans 3", category: "Sans" },
  { name: "IBM Plex Sans", category: "Sans" },
  { name: "Noto Sans", category: "Sans" },
  { name: "PT Sans", category: "Sans" },
  { name: "Ubuntu", category: "Sans" },
  { name: "Fira Sans", category: "Sans" },
  { name: "Barlow", category: "Sans" },
  { name: "Cabin", category: "Sans" },
  { name: "Quicksand", category: "Sans" },
  { name: "Josefin Sans", category: "Sans" },
  { name: "Heebo", category: "Sans" },
  { name: "Hind", category: "Sans" },
  { name: "Oxygen", category: "Sans" },
  { name: "Titillium Web", category: "Sans" },
  { name: "Archivo", category: "Sans" },
  { name: "Epilogue", category: "Sans" },
  { name: "Red Hat Display", category: "Sans" },
  { name: "Albert Sans", category: "Sans" },
  { name: "Be Vietnam Pro", category: "Sans" },
  { name: "Schibsted Grotesk", category: "Sans" },
  { name: "Instrument Sans", category: "Sans" },
  { name: "Onest", category: "Sans" },
  { name: "Assistant", category: "Sans" },
  { name: "Asap", category: "Sans" },

  // Serif
  { name: "Playfair Display", category: "Serif" },
  { name: "Merriweather", category: "Serif" },
  { name: "Lora", category: "Serif" },
  { name: "PT Serif", category: "Serif" },
  { name: "Source Serif 4", category: "Serif" },
  { name: "Libre Baskerville", category: "Serif" },
  { name: "Crimson Text", category: "Serif" },
  { name: "EB Garamond", category: "Serif" },
  { name: "Cormorant Garamond", category: "Serif" },
  { name: "Bitter", category: "Serif" },
  { name: "Domine", category: "Serif" },
  { name: "Zilla Slab", category: "Serif" },
  { name: "Roboto Slab", category: "Serif" },
  { name: "Noto Serif", category: "Serif" },
  { name: "Spectral", category: "Serif" },
  { name: "Newsreader", category: "Serif" },
  { name: "Fraunces", category: "Serif" },
  { name: "Instrument Serif", category: "Serif" },

  // Monospace
  { name: "Roboto Mono", category: "Mono" },
  { name: "Source Code Pro", category: "Mono" },
  { name: "JetBrains Mono", category: "Mono" },
  { name: "Fira Code", category: "Mono" },
  { name: "IBM Plex Mono", category: "Mono" },
  { name: "Space Mono", category: "Mono" },
  { name: "Inconsolata", category: "Mono" },
  { name: "DM Mono", category: "Mono" },

  // Display and handwriting — for a widget with more personality.
  { name: "Oswald", category: "Display" },
  { name: "Bebas Neue", category: "Display" },
  { name: "Anton", category: "Display" },
  { name: "Righteous", category: "Display" },
  { name: "Comfortaa", category: "Display" },
  { name: "Caveat", category: "Handwriting" },
  { name: "Dancing Script", category: "Handwriting" },
  { name: "Pacifico", category: "Handwriting" },
  { name: "Lobster", category: "Handwriting" },
  { name: "Satisfy", category: "Handwriting" },
];

export const FONT_CATEGORIES = ["All", "Sans", "Serif", "Mono", "Display", "Handwriting"];

const FALLBACKS = {
  Sans: "sans-serif",
  Serif: "serif",
  Mono: "monospace",
  Display: "sans-serif",
  Handwriting: "cursive",
};

const byName = new Map(FONTS.map((font) => [font.name, font]));

/** The CSS value for a family, with a fallback matching its category. */
export const fontStackFor = (family) => {
  if (!family || family === SYSTEM_FONT) {
    return "system-ui, -apple-system, 'Segoe UI', sans-serif";
  }

  const fallback = FALLBACKS[byName.get(family)?.category] || "sans-serif";
  return `'${family}', ${fallback}`;
};

// Families already requested, so a stylesheet is never injected twice.
const requested = new Set([SYSTEM_FONT]);

/**
 * Injects one Google Fonts stylesheet for whichever families aren't loaded yet.
 *
 * No `wght` axis is requested on purpose: a weight a family doesn't publish
 * makes Google reject the whole combined request, taking every other family in
 * the batch down with it. Regular loads for all of them and the browser
 * synthesises bold.
 *
 * @param {string[]} families
 */
export const loadFonts = (families) => {
  if (typeof document === "undefined") return;

  const missing = [...new Set(families)].filter(
    (family) => family && !requested.has(family) && byName.has(family),
  );

  if (!missing.length) return;

  missing.forEach((family) => requested.add(family));

  const query = missing
    .map((family) => `family=${encodeURIComponent(family).replace(/%20/g, "+")}`)
    .join("&");

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?${query}&display=swap`;
  document.head.appendChild(link);
};

export const searchFonts = (query, category = "All") => {
  const term = query.trim().toLowerCase();

  return FONTS.filter((font) => {
    if (category !== "All" && font.category !== category) return false;
    return !term || font.name.toLowerCase().includes(term);
  });
};
